if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mediaItems = Array.from(this.querySelectorAll('.product-modal-item'));
        this.track = this.querySelector('.product-modal-media-wrapper');
        
        // Modal state
        this.realCount = this.mediaItems.length - 2; 
        this.currentIndex = 1; 
        this.isTransitioning = false;
        
        // Zoom & Pan state
        this.isZoomed = false;
        this.isPanning = false;
        this.startX = 0;
        this.startY = 0;
        this.translateX = 0;
        this.translateY = 0;
        this.scale = 1;
        this.minScale = 1;
        this.maxScale = 4;

        // Interaction state
        this.activePointers = new Map();
        this.lastPinchDistance = 0;
        this.lastTapTime = 0;

        this.initNavigation();
        this.initInteraction();
        
        if (this.track) {
          this.track.addEventListener('transitionend', () => this.handleTeleport());
        }
        
        const content = this.querySelector('.product-media-modal__content');
        if (content) {
          content.addEventListener('click', (e) => e.stopPropagation());
          content.addEventListener('pointerup', (e) => e.stopPropagation());
          content.addEventListener('mousedown', (e) => e.stopPropagation());
        }

        // Global key listeners only once
        this.addEventListener('keyup', (e) => {
          if (e.code === 'ArrowRight') this.next();
          if (e.code === 'ArrowLeft') this.prev();
          if (e.code === 'Escape') this.hide();
        });
        
        // Ensure we start at the first real image (index 1)
        this.setActiveIndex(1, true);
      }

      refresh() {
        this.mediaItems = Array.from(this.querySelectorAll('.product-modal-item'));
        this.track = this.querySelector('.product-modal-media-wrapper');
        this.realCount = this.mediaItems.length - 2;
        this.currentIndex = 1;
        this.isTransitioning = false;
        
        if (this.track) {
          this.track.addEventListener('transitionend', () => this.handleTeleport());
        }

        this.resetZoom();
        this.initInteraction();
        this.setActiveIndex(1, true);
      }

      initNavigation() {
        const nextBtn = this.querySelector('.product-modal-nav-btn.next');
        const prevBtn = this.querySelector('.product-modal-nav-btn.prev');

        if (nextBtn) {
          nextBtn.addEventListener('click', (e) => { e.stopPropagation(); this.next(); });
          nextBtn.addEventListener('pointerup', (e) => e.stopPropagation());
        }
        if (prevBtn) {
          prevBtn.addEventListener('click', (e) => { e.stopPropagation(); this.prev(); });
          prevBtn.addEventListener('pointerup', (e) => e.stopPropagation());
        }
      }

      initInteraction() {
        this.mediaItems.forEach((item) => {
          const img = item.querySelector('img');
          if (!img) return;

          img.addEventListener('dragstart', (e) => e.preventDefault());

          img.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.wasPanning) return;
            // On desktop/mouse, single click toggles zoom. Touch uses double tap.
            if (this.lastPointerType === 'mouse') {
              this.toggleZoom(e, img);
            }
          });

          img.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            this.lastPointerType = e.pointerType;
            this.activePointers.set(e.pointerId, e);
            this.wasPanning = false;
            
            // Double tap detection strictly for touch
            if (e.pointerType === 'touch') {
              const now = Date.now();
              if (this.activePointers.size === 1 && now - this.lastTapTime < 300) {
                this.toggleZoom(e, img);
                this.lastTapTime = 0;
                return;
              }
              this.lastTapTime = now;
            }

            if (this.activePointers.size === 2) {
              this.lastPinchDistance = this.getPointersDistance();
            } else if (this.isZoomed && this.activePointers.size === 1) {
              this.startPan(e, img);
            }
            
            img.setPointerCapture(e.pointerId);
          });

          img.addEventListener('pointermove', (e) => {
            this.activePointers.set(e.pointerId, e);

            if (this.activePointers.size === 2) {
              this.handlePinch(img);
            } else if (this.isPanning && this.activePointers.size === 1) {
              // Differentiate between a sloppy click and an actual pan movement
              if (Math.abs(e.clientX - this.initialPanX) > 3 || Math.abs(e.clientY - this.initialPanY) > 3) {
                this.wasPanning = true;
              }
              this.doPan(e);
            }
          });

          const onPointerEnd = (e) => {
            this.activePointers.delete(e.pointerId);
            if (this.activePointers.size < 2) {
              this.lastPinchDistance = 0;
            }
            if (this.isPanning && this.activePointers.size === 0) {
              this.endPan(e, img);
            }
            if (img.hasPointerCapture(e.pointerId)) {
                img.releasePointerCapture(e.pointerId);
            }
          };

          img.addEventListener('pointerup', onPointerEnd);
          img.addEventListener('pointercancel', onPointerEnd);
          img.addEventListener('pointerleave', onPointerEnd);
        });
      }

      getPointersDistance() {
        const pointers = Array.from(this.activePointers.values());
        const dx = pointers[0].clientX - pointers[1].clientX;
        const dy = pointers[0].clientY - pointers[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }

      handlePinch(img) {
        const newDistance = this.getPointersDistance();
        if (this.lastPinchDistance === 0) {
          this.lastPinchDistance = newDistance;
          return;
        }

        const delta = newDistance / this.lastPinchDistance;
        let newScale = this.scale * delta;
        
        // Clamp scale
        newScale = Math.min(Math.max(newScale, this.minScale), this.maxScale);
        
        if (newScale !== this.scale) {
          this.scale = newScale;
          this.isZoomed = this.scale > 1;
          img.classList.toggle('is-zoomed', this.isZoomed);
          img.style.cursor = this.isZoomed ? 'grab' : 'zoom-in';
          this.updateTransform(img);
        }
        
        this.lastPinchDistance = newDistance;
      }

      show(opener) {
        this.resetZoom();
        super.show(opener);
        document.dispatchEvent(new CustomEvent('modal-opened'));
        const mediaId = opener.getAttribute('data-media-id');
        
        // Use requestAnimationFrame to ensure the modal is displayed 
        // before we try to calculate transforms or navigate.
        requestAnimationFrame(() => {
          if (mediaId) {
            this.goToMediaById(mediaId);
          } else {
            this.setActiveIndex(1, true);
          }
        });
      }

      hide() {
        this.resetZoom();
        super.hide();
        this.mediaItems.forEach(item => {
          const video = item.querySelector('video');
          const iframe = item.querySelector('iframe');
          if (video) video.pause();
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          }
        });
      }

      goToMediaById(id) {
        const index = this.mediaItems.findIndex((item, i) => i > 0 && i <= this.realCount && item.getAttribute('data-media-id') === id);
        this.setActiveIndex(index !== -1 ? index : 1, true); 
      }

      next() {
        if (this.isTransitioning) return;
        this.resetZoom();
        this.setActiveIndex(this.currentIndex + 1);
      }

      prev() {
        if (this.isTransitioning) return;
        this.resetZoom();
        this.setActiveIndex(this.currentIndex - 1);
      }

      setActiveIndex(index, immediate = false) {
        this.currentIndex = index;
        this.isTransitioning = !immediate;
        
        const offset = index * 100;
        this.track.style.transition = immediate ? 'none' : 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        this.track.style.transform = `translateX(-${offset}%)`;

        this.mediaItems.forEach((item, i) => {
          const isActive = i === index;
          item.classList.toggle('active', isActive);
          
          // Video Autoplay/Pause Logic
          const video = item.querySelector('video');
          const iframe = item.querySelector('iframe');
          
          if (isActive) {
            const deferredMedia = item.querySelector('deferred-media');
            if (deferredMedia) deferredMedia.loadContent();
            if (video) video.play().catch(() => {}); // Catch prevents error if user hasn't interacted
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
            }
          } else {
            if (video) video.pause();
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            }
          }
        });

        // Preload adjacent slides (limited to loading, not playing)
        [index - 1, index + 1].forEach(i => {
          if (this.mediaItems[i]) {
            const adjMedia = this.mediaItems[i].querySelector('deferred-media');
            if (adjMedia) adjMedia.loadContent();
          }
        });
      }

      handleTeleport() {
        this.isTransitioning = false;
        if (this.currentIndex === 0) {
          this.setActiveIndex(this.realCount, true);
        } else if (this.currentIndex === this.realCount + 1) {
          this.setActiveIndex(1, true);
        }
      }

      toggleZoom(e, img) {
        if (this.isZoomed) {
          this.resetZoom();
        } else {
          this.isZoomed = true;
          this.scale = 2.5; // Double tap or click default zoom
          img.classList.add('is-zoomed');
          img.style.cursor = 'grab';
          this.translateX = 0;
          this.translateY = 0;
          this.updateTransform(img);
        }
      }

      resetZoom() {
        this.isZoomed = false;
        this.isPanning = false;
        this.scale = 1;
        this.activePointers.clear();
        this.mediaItems.forEach(item => {
          const img = item.querySelector('img');
          if (img) {
            img.classList.remove('is-zoomed');
            img.style.transform = '';
            img.style.cursor = 'zoom-in';
          }
        });
        this.translateX = 0;
        this.translateY = 0;
      }

      startPan(e, img) {
        this.isPanning = true;
        this.startX = e.clientX - this.translateX;
        this.startY = e.clientY - this.translateY;
        this.initialPanX = e.clientX;
        this.initialPanY = e.clientY;
        img.style.cursor = 'grabbing';
      }

      doPan(e) {
        this.translateX = e.clientX - this.startX;
        this.translateY = e.clientY - this.startY;

        const activeImg = this.mediaItems[this.currentIndex].querySelector('img');
        if (activeImg) this.updateTransform(activeImg);
      }

      endPan(e, img) {
        this.isPanning = false;
        if (img) img.style.cursor = this.isZoomed ? 'grab' : 'zoom-in';
      }

      updateTransform(img) {
        if (!img) return;
        img.style.transform = `scale(${this.scale}) translate(${this.translateX / this.scale}px, ${this.translateY / this.scale}px)`;
      }
    }
  );
}
